import axios from 'axios';
import { message } from 'antd';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

const normalizeEmail = (email: string) => email.trim().toLowerCase();

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Enable cookies to be sent with requests
});

// Interceptor para refresh token automático e sessão expirada
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Try to refresh token (cookie will be sent automatically)
        const userEmail = localStorage.getItem('userEmail');
        
        if (userEmail) {
          await axios.post(`${API_URL}/api/auth/refresh`, {
            email: userEmail,
          }, {
            withCredentials: true,
          });

          // Retry original request (new cookies set automatically)
          return api(originalRequest);
        }
      } catch (refreshError) {
        // Sessão expirada - mostrar notificação e redirecionar
        message.error('Sessão expirada. Faça login novamente.');
        localStorage.removeItem('userEmail');
        localStorage.removeItem('user');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export const authService = {
  async register(data: {
    email: string;
    password: string;
    phone: string;
    name?: string;
    referralCode?: string;
    captchaToken: string;
    legalConsents: {
      termsOfService: boolean;
      privacyPolicy: boolean;
    };
  }) {
    const response = await api.post('/api/auth/register', {
      ...data,
      email: normalizeEmail(data.email),
    });
    return response.data;
  },

  async startRegisterPhoneTransfer(data: {
    email: string;
    password: string;
    phone: string;
    name?: string;
    referralCode?: string;
    legalConsents: {
      termsOfService: boolean;
      privacyPolicy: boolean;
    };
  }) {
    const response = await api.post('/api/auth/register/phone-transfer/start', {
      ...data,
      email: normalizeEmail(data.email),
    });
    return response.data as {
      message: string;
      email: string;
      transferSessionId: number;
      expiresAt: string;
    };
  },

  async verifyRegisterPhoneTransfer(data: {
    email: string;
    transferSessionId: number;
    otpCode: string;
  }) {
    const response = await api.post('/api/auth/register/phone-transfer/verify', {
      ...data,
      email: normalizeEmail(data.email),
    });
    return response.data;
  },

  async confirmSignUp(email: string, code: string) {
    const response = await api.post('/api/auth/confirm', { email: normalizeEmail(email), code });
    return response.data;
  },

  async resendConfirmationCode(email: string) {
    const response = await api.post('/api/auth/resend-code', { email: normalizeEmail(email) });
    return response.data;
  },

  async login(email: string, password: string, captchaToken: string) {
    const normalizedEmail = normalizeEmail(email);
    const response = await api.post('/api/auth/login', { email: normalizedEmail, password, captchaToken });

    // Store only user email and data (tokens are in httpOnly cookies)
    if (response.data.user) {
      localStorage.setItem('userEmail', normalizeEmail(response.data.user.email));
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }

    return response.data;
  },

  async loginWithGoogle(idToken: string, referralCode?: string) {
    const response = await api.post('/api/auth/google', { googleIdToken: idToken, referralCode });

    if (response.data.user) {
      localStorage.setItem('userEmail', normalizeEmail(response.data.user.email));
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }

    return response.data as {
      message: string;
      isNewAccount: boolean;
      user: { email: string; preferredName: string; role: string };
    };
  },

  async setPassword(newPassword: string) {
    const response = await api.post('/api/auth/set-password', { newPassword });
    return response.data;
  },

  async refreshToken() {
    const userEmail = localStorage.getItem('userEmail');
    if (!userEmail) {
      throw new Error('No user email found');
    }

    const response = await api.post('/api/auth/refresh', {
      email: userEmail,
    });

    return response.data;
  },

  async forgotPassword(email: string) {
    const response = await api.post('/api/auth/forgot-password', { email: normalizeEmail(email) });
    return response.data;
  },

  async confirmPassword(email: string, code: string, newPassword: string) {
    const response = await api.post('/api/auth/confirm-password', {
      email: normalizeEmail(email),
      code,
      newPassword,
    });
    return response.data;
  },

  async logout() {
    try {
      // Call backend to clear cookies and invalidate session
      await api.post('/api/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local storage (cookies are cleared by backend)
      localStorage.removeItem('userEmail');
      localStorage.removeItem('user');
    }
  },

  async getCurrentUser() {
    const response = await api.get('/api/auth/me');
    return response.data.user;
  },

  isAuthenticated(): boolean {
    // Check if user data exists (cookies are httpOnly, can't check them)
    return !!localStorage.getItem('user');
  },

  getUser() {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  },
};

export default api;